// components/ListarNotificacoes.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { Image } from 'primereact/image';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/pt-br';
import NotificationService from '../../service/NotificationService';

moment.locale('pt-br');

const notificationService = new NotificationService();

const ListarNotificacoes = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1
    });
    const history = useHistory();
    const toast = React.useRef(null);

    const iss = localStorage.getItem('iss');
    let roles = JSON.parse(localStorage.getItem('roles'));

    const loadNotifications = useCallback(() => {
        setLoading(true);
        try {
            notificationService.listNotifications(iss, lazyParams).then(data => {
                setNotifications(data.data.data);
                setTotalRecords(data.data.pagination.total);
                setLoading(false);
            });

        } catch (error) {
            console.error('Erro ao carregar notificações:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Erro ao carregar notificações',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    }, [iss, lazyParams]);

    useEffect(() => {
        loadNotifications();
    }, [lazyParams, lazyParams.page, lazyParams.rows, loadNotifications]);

    const onPage = (event) => {
        const newLazyParams = {
            ...lazyParams,
            first: event.first,
            rows: event.rows,
            page: Math.floor(event.first / event.rows) + 1
        };
        setLazyParams(newLazyParams);
    };

    // ==================== TEMPLATES DE COLUNAS ====================

    const imageBodyTemplate = (rowData) => {
        if (!rowData.imageUrl) {
            return (
                <div className="flex align-items-center justify-content-center"
                    style={{ width: '60px', height: '60px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
                    <i className="pi pi-image" style={{ fontSize: '1.5rem', color: '#999' }}></i>
                </div>
            );
        }

        return (
            <Image
                src={rowData.imageUrl}
                alt={rowData.title}
                width="60"
                height="60"
                preview
                imageStyle={{ borderRadius: '8px', objectFit: 'cover' }}
            />
        );
    };

    const titleBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="font-bold text-lg mb-1">{rowData.title}</div>
                <div className="text-sm text-500 line-clamp-2">{rowData.body}</div>
            </div>
        );
    };

    const dateBodyTemplate = (rowData) => {
        return (
            <div>
                <div className="font-semibold">{moment(rowData.sentAt).format('DD/MM/YYYY')}</div>
                <div className="text-sm text-500">{moment(rowData.sentAt).format('HH:mm')}</div>
                <div className="text-xs text-400 mt-1">{moment(rowData.sentAt).fromNow()}</div>
            </div>
        );
    };

    const statsBodyTemplate = (rowData) => {
        const getColor = (percentage) => {
            if (percentage >= 70) return 'success';
            if (percentage >= 40) return 'warning';
            return 'danger';
        };

        return (
            <div className="flex flex-column gap-2">
                <div className="flex justify-content-between align-items-center">
                    <span className="text-sm font-semibold">
                        {rowData.totalRead} / {rowData.totalRecipients} lidas
                    </span>
                    <Tag
                        value={`${rowData.readPercentage}%`}
                        severity={getColor(rowData.readPercentage)}
                        rounded
                    />
                </div>
                <ProgressBar
                    value={rowData.readPercentage}
                    showValue={false}
                    style={{ height: '8px' }}
                    color={
                        rowData.readPercentage >= 70 ? '#22c55e' :
                            rowData.readPercentage >= 40 ? '#f59e0b' : '#ef4444'
                    }
                />
                <div className="flex gap-3 text-xs text-500">
                    <span>
                        <i className="pi pi-eye text-green-500 mr-1"></i>
                        {rowData.totalRead} lidas
                    </span>
                    <span>
                        <i className="pi pi-eye-slash text-orange-500 mr-1"></i>
                        {rowData.totalUnread} não lidas
                    </span>
                </div>
            </div>
        );
    };

    const audienceBodyTemplate = (rowData) => {
        return (
            <Tag
                value={rowData.targetAudience === 'all' ? 'Todos' : 'Específico'}
                severity={rowData.targetAudience === 'all' ? 'info' : 'warning'}
                icon={rowData.targetAudience === 'all' ? 'pi pi-users' : 'pi pi-user'}
            />
        );
    };

    const actionsBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Tooltip target=".view-button" content="Ver Detalhes" position="top" />
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-info p-button-sm view-button"
                    onClick={() => handleViewDetails(rowData.id)}
                />

                {/* <Tooltip target=".resend-button" content="Reenviar" position="top" />
                <Button
                    icon="pi pi-refresh"
                    className="p-button-rounded p-button-success p-button-sm resend-button"
                    onClick={() => handleResend(rowData.id)}
                />

                <Tooltip target=".delete-button" content="Excluir" position="top" />
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger p-button-sm delete-button"
                    onClick={() => confirmDelete(rowData)}
                /> */}
            </div>
        );
    };

    // ==================== AÇÕES ====================

    const handleViewDetails = (notificationId) => {
        // Navegar para tela de detalhes (implementar depois)
        console.log('Ver detalhes:', notificationId);
        history.push('/notificacoes/detalhe/' + notificationId)
    };

    const handleResend = async (notificationId) => {
        // Implementar reenvio (implementar depois)
        console.log('Reenviar:', notificationId);
        toast.current?.show({
            severity: 'info',
            summary: 'Em desenvolvimento',
            detail: 'Funcionalidade de reenvio será implementada',
            life: 3000
        });
    };

    const confirmDelete = (notification) => {
        confirmDialog({
            message: `Deseja realmente excluir a notificação "${notification.title}"?`,
            header: 'Confirmação de Exclusão',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sim, excluir',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: () => handleDelete(notification.id)
        });
    };

    const handleDelete = async (notificationId) => {
        try {
            await axios.delete(`/api/v1/notifications/${notificationId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                }
            });

            toast.current?.show({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Notificação excluída com sucesso',
                life: 3000
            });

            loadNotifications();
        } catch (error) {
            console.error('Erro ao excluir:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: 'Erro ao excluir notificação',
                life: 3000
            });
        }
    };

    // ==================== HEADER ====================

    const header = (
        <div className="flex justify-content-between align-items-center">
            <div>
                <h2 className="m-0">📬 Notificações Enviadas</h2>
                <p className="text-500 mt-1">Gerencie e monitore suas notificações push</p>
            </div>
            <Button
                label="Nova Notificação"
                icon="pi pi-plus"
                className="p-button-success"
                onClick={() => history.push('/notificacoes/criar')}
            />
        </div>
    );

    // ==================== RENDER ====================

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <Card>
                <DataTable
                    value={notifications}
                    lazy
                    paginator
                    first={lazyParams.first}
                    rows={lazyParams.rows}
                    totalRecords={totalRecords}
                    onPage={onPage}
                    loading={loading}
                    header={header}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} notificações"
                    emptyMessage="Nenhuma notificação enviada ainda"
                    responsiveLayout="scroll"
                    stripedRows
                    rowHover
                >
                    <Column
                        header="Imagem"
                        body={imageBodyTemplate}
                        style={{ width: '80px' }}
                    />

                    <Column
                        header="Título e Mensagem"
                        body={titleBodyTemplate}
                        style={{ minWidth: '250px' }}
                    />

                    <Column
                        header="Data de Envio"
                        body={dateBodyTemplate}
                        style={{ width: '150px' }}
                        sortable
                    />

                    <Column
                        header="Estatísticas"
                        body={statsBodyTemplate}
                        style={{ minWidth: '250px' }}
                    />

                    <Column
                        header="Público"
                        body={audienceBodyTemplate}
                        style={{ width: '120px' }}
                    />

                    <Column
                        header="Ações"
                        body={actionsBodyTemplate}
                        style={{ width: '150px' }}
                        frozen
                        alignFrozen="right"
                    />
                </DataTable>
            </Card>
        </div>
    );
};

export default ListarNotificacoes;